import { motion } from "framer-motion";
import { Gamepad2, Play, Rocket, Server } from "lucide-react";
import { useLanguage } from "@/i18n";
import { ASSETS } from "@/data";
import { PixelAvatar } from "@/components/PixelAvatar";

const STATUS_STYLE = {
  production: "border-amber-400/40 bg-amber-400/10 text-amber-300",
  soon: "border-purple-500/40 bg-purple-500/10 text-purple-300",
  launch: "border-cyan-400/40 bg-cyan-400/10 text-cyan-300",
};

export default function Products() {
  const { t } = useLanguage();
  const statusLabel = { production: t.products.statusProduction, soon: t.products.statusSoon, launch: t.products.statusLaunch };

  return (
    <section id="products" data-testid="products-section" className="relative scroll-mt-20 overflow-hidden bg-[#070A1C] py-24 sm:py-32">
      <div className="grid-bg pointer-events-none absolute inset-0 opacity-40" />
      <div className="pointer-events-none absolute -right-40 top-0 h-96 w-96 rounded-full bg-purple-600/15 blur-[130px]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
        >
          <div>
            <span className="font-pixel text-[10px] tracking-[0.4em] text-purple-400">// {t.products.label}</span>
            <h2 className="font-display mt-4 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              {t.products.title}
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">{t.products.subtitle}</p>
        </motion.div>

        <motion.article
          data-testid="product-card-game"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          className="group grid grid-cols-1 overflow-hidden border border-white/10 bg-[#0A0D24]/80 lg:grid-cols-5"
        >
          <div className="relative lg:col-span-3">
            <video
              data-testid="game-video"
              src={ASSETS.gameVideo}
              autoPlay
              muted
              loop
              playsInline
              controls
              className="h-full max-h-[420px] w-full bg-black object-cover"
            />
            <span
              className={`font-pixel absolute left-4 top-4 border px-3 py-1.5 text-[9px] tracking-[0.25em] backdrop-blur-sm ${STATUS_STYLE.production}`}
              data-testid="game-status-badge"
            >
              {statusLabel.production}
            </span>
          </div>
          <div className="flex flex-col justify-center p-8 sm:p-10 lg:col-span-2">
            <div className="mb-5 flex items-center gap-3">
              <span className="pixel-corners-sm flex h-11 w-11 items-center justify-center border border-purple-500/40 bg-purple-500/10 text-purple-300">
                <Gamepad2 className="h-5 w-5" />
              </span>
              <span className="font-pixel text-[9px] tracking-[0.3em] text-slate-500">NAVX GAME LAB</span>
            </div>
            <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{t.products.gameTitle}</h3>
            <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">{t.products.gameDesc}</p>
            <div className="font-pixel mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] text-cyan-300">
              <Play className="h-3.5 w-3.5" />
              {t.products.watchNow}
            </div>
          </div>
        </motion.article>

        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          {t.products.others.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`product-card-${p.id}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              className="group relative flex items-start gap-6 overflow-hidden border border-white/10 bg-[#0A0D24]/70 p-8 transition-all duration-500 hover:-translate-y-1 hover:border-purple-400/50 hover:shadow-[0_20px_60px_-24px_rgba(139,92,246,0.45)]"
            >
              <div className="relative shrink-0">
                <PixelAvatar seed={p.name} className="h-20 w-20 border border-white/10" />
                <span className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center border border-cyan-400/40 bg-[#050714] text-cyan-300">
                  {p.status === "launch" ? <Rocket className="h-4 w-4" /> : <Server className="h-4 w-4" />}
                </span>
              </div>
              <div>
                <span
                  data-testid={`product-status-${p.id}`}
                  className={`font-pixel inline-block border px-2.5 py-1 text-[9px] tracking-[0.25em] ${STATUS_STYLE[p.status]}`}
                >
                  {statusLabel[p.status]}
                </span>
                <h3 className="font-display mt-3 text-lg font-bold text-white sm:text-xl">{p.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.desc}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
